import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Input, Table } from 'antd';
import { RightOutlined, SearchOutlined } from '@ant-design/icons';
import { FaArrowRightLong } from 'react-icons/fa6';
import moment from 'moment';
import { convertSnakeCaseToTitleCase } from 'utils/string.utils';
import { AppTableInitialState, AppTableSlice } from 'components/AppTable/store';
import { RootState } from 'store';
import ReportTable, { ReportColumn } from './ReportTable';
import { ProfileDateRangeProps } from './useProfileDateRange';
import profileTableStyles from './profileTable.module.less';

interface EventDateConfig {
  title: string;
  single: string;
  old: string;
  next: string;
}

interface OverdueReportProps extends ProfileDateRangeProps {
  storeSlice: AppTableSlice;
  userUuid: string;
  rowKey: string;
  titleColumn: { title: string; dataIndex: string };
  searchField: string;
  searchPlaceholder: string;
  showSearch?: boolean;
  titleLink?: (row: any) => string;
  eventDate: EventDateConfig;
  countLabel: string;
  headerTitle: string;
  headerSubtitle: string;
}

const formatDateTime = (value?: string) => (value ? moment(value, 'YYYY-MM-DD HH:mm:ss').format('MMM DD, YYYY, HH:mm') : '-');
const formatDate = (value?: string) => (value ? moment(value, 'YYYY-MM-DD').format('MMM DD, YYYY') : '-');

const renderEventTag = (type: string) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      border: '1px solid #e8e8e8',
      borderRadius: 8,
      padding: '2px 8px',
      fontSize: 12,
      fontWeight: 500,
    }}
  >
    {convertSnakeCaseToTitleCase(type, 'first-letter')}
  </span>
);

const renderEventsCount = (events: any) => {
  const count = Array.isArray(events) ? events.length : 0;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        height: 22,
        padding: '0 8px',
        borderRadius: 11,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: 'transparent',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      }}
    >
      {count}
    </span>
  );
};

const OverdueReport: FC<OverdueReportProps> = ({
  storeSlice,
  userUuid,
  rowKey,
  titleColumn,
  searchField,
  searchPlaceholder,
  showSearch = true,
  titleLink,
  eventDate,
  countLabel,
  headerTitle,
  headerSubtitle,
  dateRange,
  onDateRangeChange,
}) => {
  const [search, setSearch] = useState('');
  const [expandedRowKeys, setExpandedRowKeys] = useState<any[]>([]);

  const data = useSelector((store: RootState) => (store[storeSlice.name] as AppTableInitialState).data);

  useEffect(() => {
    setExpandedRowKeys([]);
  }, [data]);

  const renderEventDate = (event: any) => {
    if (event?.[eventDate.old] && event?.[eventDate.next]) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {formatDate(event[eventDate.old])}
          <FaArrowRightLong />
          {formatDate(event[eventDate.next])}
        </span>
      );
    }

    if (event?.[eventDate.single]) {
      return formatDate(event[eventDate.single]);
    }

    return '-';
  };

  const eventColumns = [
    {
      title: 'Changed at',
      dataIndex: 'date_time',
      key: 'date_time',
      width: '25%',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'Changed by',
      dataIndex: 'responsible_user',
      key: 'changed_by',
      width: '25%',
      render: (user: any, event: any) => user?.name ?? event?.changed_by ?? '-',
    },
    {
      title: eventDate.title,
      dataIndex: eventDate.single,
      key: 'event_date',
      width: '25%',
      render: (_value: any, event: any) => renderEventDate(event),
    },
    { title: 'Event', dataIndex: 'type', key: 'type', width: '25%', render: (type: string) => renderEventTag(type) },
  ];

  const expandedRowRender = (row: any) => (
    <Table
      size="small"
      columns={eventColumns}
      dataSource={row?.events ?? []}
      rowKey={(_event, index) => String(index)}
      pagination={false}
    />
  );

  const renderTitle = (value: any, row: any) => {
    if (titleLink) {
      return (
        <a
          href={titleLink(row)}
          target="_blank"
          rel="noopener noreferrer"
          className={profileTableStyles.subjectLink}
        >
          {value ?? '-'}
        </a>
      );
    }

    return <span>{value ?? '-'}</span>;
  };

  const columns: ReportColumn[] = [
    { title: titleColumn.title, dataIndex: titleColumn.dataIndex, width: '50%', render: renderTitle },
    { title: 'Customer', dataIndex: 'customer', width: '30%', render: (value) => <span>{value ?? '-'}</span> },
    { title: 'Events', dataIndex: 'events', width: '20%', render: renderEventsCount },
  ];

  const filterRows = useCallback(
    (rows: Record<string, any>[]) =>
      rows.filter((row) => {
        const matchesSearch = !search || String(row[searchField] ?? '').toLowerCase().includes(search.toLowerCase());

        return matchesSearch;
      }),
    [search, searchField],
  );

  const extraFilters: ReactNode = showSearch ? (
    <Input
      allowClear
      size="large"
      placeholder={searchPlaceholder}
      prefix={<SearchOutlined />}
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      style={{ width: 220 }}
    />
  ) : null;

  const headerContent: ReactNode = (
    <div>
      <div style={{ fontWeight: 600, fontSize: '1rem' }}>{headerTitle}</div>
      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '0.875rem' }}>{headerSubtitle}</div>
    </div>
  );

  return (
    <ReportTable
      storeSlice={storeSlice}
      columns={columns}
      userUuid={userUuid}
      dateRange={dateRange}
      onDateRangeChange={onDateRangeChange}
      countLabel={countLabel}
      rowKey={rowKey}
      headerContent={headerContent}
      expandable={{
        expandedRowKeys,
        onExpand: (expanded, record) =>
          setExpandedRowKeys((keys) =>
            expanded ? [...keys, record[rowKey]] : keys.filter((key) => key !== record[rowKey])),
        expandedRowRender,
        expandIcon: ({ expanded, onExpand, record }) => (
          <RightOutlined
            onClick={(event) => onExpand(record, event)}
            style={{ cursor: 'pointer', transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}
          />
        ),
      }}
      onRenderData={filterRows}
      applyRenderDataBeforePagination
      extraFilters={extraFilters}
    />
  );
};

export default OverdueReport;
